import { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import { IdleTimeout } from '../../controls/IdleTimeout/IdleTimeout';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import {
    PageInitialState,
    Types,
    PageReducerFunction,
    PageProviderComponent,
    UsePageStateFunction,
    UsePageDispatchFunction,
    UseShowKioskBackgroundFunction,
    UseHideKioskBackgroundFunction,
    UseSetKioskBackgroundVisibilityFunction,
    UseSetKioskBannerVisibilityFunction,
    UseSetHeaderPropsFunction,
    UseSetIdleDialogOpenFunction,
    PageState,
    PageDispatch,
} from './page.types';

// TODO: Finish restructuring and writing tests/comments/mocks

export const pageReducer: PageReducerFunction = (state, action) => {
    switch (action.type) {
        case Types.ShowKioskBackground:
            return {
                ...state,
                showKioskBackground: true,
            };
        case Types.HideKioskBackground:
            return {
                ...state,
                showKioskBackground: false,
            };
        case Types.SetKioskBackgroundVisibility:
            return {
                ...state,
                showKioskBackground: action.payload,
            };
        case Types.HideIdleTimeoutDialog:
            return {
                ...state,
                hideIdleTimeoutDialog: action.payload,
            };
        case Types.SetEvokeOnStillActive:
            return {
                ...state,
                evokeOnStillActive: action.payload,
            };
        case Types.SetEvokeOnTimeout:
            return {
                ...state,
                evokeOnTimeout: action.payload,
            };
        case Types.SetIdleDialogOpen:
            return {
                ...state,
                idleDialogOpen: action.payload,
            };
        case Types.SetKioskBannerVisibility:
            return {
                ...state,
                showKioskBanner: action.payload,
            };
        case Types.SetHeaderProps:
            return {
                ...state,
                headerProps: action.payload,
            };
        case Types.UpdateState:
            return {
                ...state,
                ...action.payload,
            };
        default:
            return state;
    }
};

const initialState: PageInitialState = {
    showKioskBackground: true,
    hideIdleTimeoutDialog: false,
    evokeOnStillActive: undefined,
    evokeOnTimeout: undefined,
    idleDialogOpen: false,
    showKioskBanner: true,
    headerProps: undefined,
};

const PageStateContext = createContext<PageState>(undefined);
const PageDispatchContext = createContext<PageDispatch>(undefined);

export const PageProvider: PageProviderComponent = ({
    showKioskBackground = true,
    showKioskBanner = true,
    headerProps = undefined,
    suppressTimeout = false,
    children,
}) => {
    const [state, dispatch] = useReducer(pageReducer, {
        ...initialState,
        showKioskBackground,
        showKioskBanner,
        headerProps,
    });

    useEffect(() => {
        dispatch({
            type: Types.UpdateState,
            payload: {
                ...initialState,
                showKioskBackground,
                showKioskBanner,
                headerProps,
            },
        });
    }, [headerProps, showKioskBackground, showKioskBanner]);

    return (
        <PageStateContext.Provider value={state}>
            <PageDispatchContext.Provider value={dispatch}>
                {isElectron() && !suppressTimeout ?
                    <IdleTimeout
                        hideDialog={state.hideIdleTimeoutDialog}
                        evokeOnStillActive={state.evokeOnStillActive}
                        evokeOnTimeout={state.evokeOnTimeout}
                    >
                        {children}
                    </IdleTimeout>
                    : children
                }
            </PageDispatchContext.Provider>
        </PageStateContext.Provider>
    );
};

export const usePageState: UsePageStateFunction = () => {
    const context = useContext(PageStateContext);
    if (!context) {
        throw new Error('usePageState must be used within a PageProvider');
    }

    return context;
};

export const usePageDispatch: UsePageDispatchFunction = () => {
    const context = useContext(PageDispatchContext);
    if (!context) {
        throw new Error('usePageDispatch must be used within a PageProvider');
    }

    return context;
};

export const useShowKioskBackground: UseShowKioskBackgroundFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback(() => {
        dispatch({ type: Types.ShowKioskBackground });
    }, [dispatch]);
};

export const useHideKioskBackground: UseHideKioskBackgroundFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback(() => {
        dispatch({ type: Types.HideKioskBackground });
    }, [dispatch]);
};

export const useSetKioskBackgroundVisibility: UseSetKioskBackgroundVisibilityFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback((visibile) => {
        dispatch({
            type: Types.SetKioskBackgroundVisibility,
            payload: visibile,
        });
    }, [dispatch]);
};

export const useSetKioskBannerVisibility: UseSetKioskBannerVisibilityFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback((visibile) => {
        dispatch({
            type: Types.SetKioskBannerVisibility,
            payload: visibile,
        });
    }, [dispatch]);
};

export const useSetHeaderProps: UseSetHeaderPropsFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback((headerProps) => {
        dispatch({
            type: Types.SetHeaderProps,
            payload: headerProps,
        });
    }, [dispatch]);
};

/**
 * Publishes whether the kiosk Session Timeout dialog is currently open.
 *
 * @returns A setter that records the open/closed state of the Session Timeout dialog.
 */
export const useSetIdleDialogOpen: UseSetIdleDialogOpenFunction = () => {
    const dispatch = usePageDispatch();

    return useCallback((open: boolean) => {
        dispatch({
            type: Types.SetIdleDialogOpen,
            payload: open,
        });
    }, [dispatch]);
};
